import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";

import { cn } from "@/lib/utils";
import AITiptapEditor from "@/components/Form/TiptapEditor/AITiptapEditor";
import {
  RichTextValue,
  getTemplateStringValue,
} from "@/components/Form/RichTextField";
import { NoxusFormEntryConfig } from "@/components/Spotflow/ConfigDrawer/ConfigDrawer";
import { ConfigRichTextVariablesAI } from "@/types/api/spotflow/spotflow-config";
import type { NoxusTipTapDocumentInput } from "@/generated";

type Props = {
  value: string | RichTextValue | RichTextValue[] | NoxusTipTapDocumentInput;
  onChange: (value: RichTextValue) => void;
  isDisabled?: boolean;
  label?: string;
  placeholder?: string;
  fieldConfig?: NoxusFormEntryConfig;
  defaultExpanded?: boolean;
  isFlush?: boolean;
};

export function ExpandableTextarea({
  value,
  onChange,
  isDisabled,
  label = "Tool usage conditions",
  placeholder = "Specify when and how this tool should be used...",
  fieldConfig,
  defaultExpanded = false,
  isFlush = false,
}: Props) {
  const richTextConfig =
    fieldConfig?.display?.type === "rich_text_variables_ai"
      ? (fieldConfig.display as ConfigRichTextVariablesAI)
      : undefined;
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  const parsedValue = useMemo((): RichTextValue[] => {
    if (!value) return [];
    const arrayedValue = Array.isArray(value) ? value : [value];
    return arrayedValue.map((v) => {
      if (
        typeof v === "string" ||
        (typeof v === "object" &&
          v !== null &&
          "text" in v &&
          !("definition" in v))
      ) {
        return getTemplateStringValue(v);
      }
      return {
        text: v.text,
        definition: (v.definition ?? {}) as Record<string, unknown>,
      };
    });
  }, [value]);

  // Track the last value we emitted so we can detect external changes
  // (e.g. modal save) vs our own onChange updates.
  const currentText = parsedValue[parsedValue.length - 1]?.text ?? "";
  const lastEmittedTextRef = useRef<string>(currentText);
  const [editorKey, setEditorKey] = useState(0);

  useEffect(() => {
    if (currentText !== lastEmittedTextRef.current) {
      lastEmittedTextRef.current = currentText;
      setEditorKey((k) => k + 1);
    }
  }, [currentText]);

  const handleChange = (newValue: RichTextValue[]) => {
    const current = newValue[newValue.length - 1];
    if (current) {
      lastEmittedTextRef.current = current.text;
      onChange(current);
    }
  };

  return (
    <div
      className={cn(
        "border-t bg-white",
        isFlush ? "border-input" : "border-border/50"
      )}
    >
      {!defaultExpanded && (
        <button
          type="button"
          className="flex items-center justify-between w-full px-3 py-2 text-left transition-colors cursor-pointer"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <span className="text-xs font-medium text-muted-foreground">
            {label}
          </span>
          <ChevronDown
            className={cn(
              "h-4 w-4 text-muted-foreground transition-transform",
              isExpanded && "rotate-180"
            )}
          />
        </button>
      )}

      <div
        className={cn(
          "grid transition-all duration-200 ease-out",
          isExpanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
        )}
      >
        <div className="overflow-hidden">
          <div
            className={cn(
              "transition-all duration-200 ease-out",
              isFlush ? "" : "px-3 pt-1 pb-3",
              isExpanded ? "opacity-100" : "opacity-0"
            )}
          >
            <AITiptapEditor
              isFlush={isFlush}
              key={editorKey}
              value={parsedValue}
              onChange={handleChange}
              isControlled
              isDisabled={isDisabled || !isExpanded}
              placeholder={richTextConfig?.placeholder ?? placeholder}
              label={label}
              enableHeuristics={richTextConfig?.enable_heuristics}
              heuristicContext={richTextConfig?.heuristic_context ?? undefined}
              heuristicTypes={richTextConfig?.heuristic_types ?? undefined}
              improverContext={richTextConfig?.improver_context ?? undefined}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
